import React, { useState, useEffect } from 'react';
import { Card, Tabs, List, Typography, Tag, Empty, Collapse } from 'antd';
import { useTranslation } from 'react-i18next';
import { useAccount } from 'wagmi';
import api from '../api/axios';

const { Text } = Typography;
const { TabPane } = Tabs;
const { Panel } = Collapse;

// 优化后的样式
const styles = {
  card: {
    backgroundColor: 'rgba(20, 30, 40, 0.6)',
    backdropFilter: 'blur(15px)',
    border: '1px solid rgba(100,255,218,0.08)',
  },
  collapse: {
    backgroundColor: 'rgba(36,54,66,0.85)',
    borderRadius: '8px',
    border: 'none',
    boxShadow: 'none',
    margin: 0,
    padding: 0,
  },
  panel: {
    backgroundColor: 'rgba(36,54,66,0.85)',
    borderRadius: '8px',
    padding: 0,
    margin: 0,
    border: 'none',
    boxShadow: 'none',
  },
  collapseHeader: {
    color: '#64ffda',
    fontSize: '15px',
    fontWeight: 500,
    backgroundColor: 'rgba(36,54,66,0.85)',
    borderRadius: '8px 8px 0 0',
    padding: '8px 16px',
    width: '100%',
    display: 'block',
    minHeight: 'unset',
    lineHeight: '1.2',
  },
  listItem: {
    padding: '12px 16px',
    marginBottom: '8px',
    color: '#fff',
    fontSize: '14px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid rgba(100,255,218,0.08)',
    background: 'rgba(36,54,66,0.85)',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  }
};

interface Record {
  amount: string;
  time: string;
  interest_rate?: string;
  status?: number;
}

interface StatusMap {
  [key: number]: {
    color: string;
    text: string;
  };
}

const RecordCard: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { address } = useAccount();
  const [earningsRecords, setEarningsRecords] = useState<Record[]>([]);
  const [withdrawalRecords, setWithdrawalRecords] = useState<Record[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('earnings');
  const [activeKeys, setActiveKeys] = useState<string[]>([]);

  const fetchEarningsRecords = async () => {
    if (!address) return;
    setLoading(true);
    try {
      const response = await api.get(`/records/earnings?wallet_address=${address}`);
      if (response.data.code === 200) {
        setEarningsRecords(response.data.data);
      }
    } catch (error) {
      console.error('获取收益记录失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWithdrawalRecords = async () => {
    if (!address) return;
    setLoading(true);
    try {
      const response = await api.get(`/records/withdrawals?wallet_address=${address}`);
      if (response.data.code === 200) {
        setWithdrawalRecords(response.data.data);
      }
    } catch (error) {
      console.error('获取提现记录失败:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (address) {
      if (activeTab === 'earnings') {
        fetchEarningsRecords();
      } else {
        fetchWithdrawalRecords();
      }
    }
  }, [address, activeTab]);

  const formatTime = (time: string) => {
    const date = new Date(time);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusTag = (status: number) => {
    const statusMap: StatusMap = {
      0: { color: 'warning', text: t('records.status.processing') },
      1: { color: 'success', text: t('records.status.success') },
      2: { color: 'error', text: t('records.status.failed') }
    };
    const { color, text } = statusMap[status] || { color: 'default', text: t('records.status.unknown') };
    return <Tag color={color}>{text}</Tag>;
  };

  // 让emptyText在切换语言时强制刷新
  const EmptyText = () => (
    <Empty
      description={
        <span style={{ color: '#64ffda', fontSize: 18, fontWeight: 500 }}>
          {t('records.noData')}
        </span>
      }
    />
  );

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    setActiveKeys([]); // Reset active keys when switching tabs
  };

  const handleCollapseChange = (keys: string[]) => {
    setActiveKeys(keys);
  };

  const renderEarningsList = () => (
    <List
      loading={loading}
      dataSource={earningsRecords}
      locale={{ emptyText: <EmptyText key={i18n.language} /> }}
      renderItem={(record) => (
        <List.Item>
          <div style={{ ...styles.listItem, width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Text className="record-amount" style={{ color: '#fff' }}>{record.amount} USDC</Text>
              <br />
              <Text type="secondary" style={{ color: '#64ffda' }}>{t('records.interestRate')}: {record.interest_rate}%</Text>
            </div>
            <Text type="secondary" style={{ color: '#fff' }}>{formatTime(record.time)}</Text>
          </div>
        </List.Item>
      )}
    />
  );

  const renderWithdrawalsList = () => (
    <List
      loading={loading}
      dataSource={withdrawalRecords}
      locale={{ emptyText: <EmptyText key={i18n.language} /> }}
      renderItem={(record) => (
        <List.Item>
          <div style={{ ...styles.listItem, width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Text className="record-amount" style={{ color: '#fff' }}>{record.amount} USDC</Text>
              <br />
              {getStatusTag(record.status!)}
            </div>
            <Text type="secondary" style={{ color: '#fff' }}>{formatTime(record.time)}</Text>
          </div>
        </List.Item>
      )}
    />
  );

  return (
    <Card
      title={<span className="record-card-title">{t('records.title')}</span>}
      className="record-card"
      style={{ ...styles.card, marginBottom: '20px' }}
    >
      <Tabs defaultActiveKey="earnings" onChange={handleTabChange}>
        <TabPane tab={t('records.earnings')} key="earnings">
          <Collapse 
            activeKey={activeKeys} 
            onChange={handleCollapseChange} 
            style={styles.collapse}
            expandIconPosition="end"
          >
            <Panel 
              header={<div style={styles.collapseHeader}>{t('records.list')}</div>} 
              key="earnings" 
              style={styles.panel}
              showArrow={true}
            >
              {renderEarningsList()}
            </Panel>
          </Collapse>
        </TabPane>
        <TabPane tab={t('records.withdrawals')} key="withdrawals">
          <Collapse 
            activeKey={activeKeys} 
            onChange={handleCollapseChange} 
            style={styles.collapse}
            expandIconPosition="end"
          >
            <Panel 
              header={<div style={styles.collapseHeader}>{t('records.list')}</div>} 
              key="withdrawals" 
              style={styles.panel}
              showArrow={true}
            >
              {renderWithdrawalsList()}
            </Panel>
          </Collapse>
        </TabPane>
      </Tabs>
    </Card>
  );
};

export default RecordCard;

<style>
  {`
    .record-card .ant-collapse-content,
    .record-card .ant-collapse-content-box {
      background: rgba(36,54,66,0.85) !important;
      padding: 0 !important;
    }
    .record-card .ant-list-item {
      border-bottom: none !important;
      background: transparent !important;
      margin: 0 !important;
      padding-left: 0 !important;
      padding-right: 0 !important;
    }
    .record-card .ant-list {
      background: transparent !important;
    }
  `}
</style> 